/**
 * Hook for organization service operations
 */

import { useState, useEffect } from 'react';
import { FirestoreService } from '@/services/firestore';
import { useAuth } from '@/hooks/useAuth';
import type { Organization } from '@/types/organization';

const organizationService = new FirestoreService<Organization>('organizations');

export const useOrganizationService = () => {
  const { isAuthenticated, isApproved, loading: authLoading } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationService.getAll();
      setOrganizations(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error loading organizations'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load data when authentication is complete and user is approved
    if (!authLoading && isAuthenticated && isApproved) {
      loadOrganizations();
    } else if (!authLoading && (!isAuthenticated || !isApproved)) {
      // Reset state if user is not authenticated or approved
      setOrganizations([]);
      setLoading(false);
      setError(null);
    }
  }, [authLoading, isAuthenticated, isApproved]);

  return {
    organizations,
    loading,
    error,
    refetch: loadOrganizations,
  };
};
